import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Shield, AlertCircle, Edit, CalendarX, ClipboardCheck } from 'lucide-react';
import { GenericListView, FormPageLayout, StatusPill, getStatusPillClassName, Banner } from '../common';
import type { ActionButtonConfig } from '../common';
import {
  InsuranceService,
  InsuranceCoverage,
  CreateInsuranceCoverageRequest,
  CoverageType,
  COBOrder,
  CoverageStatus
} from '../../services/patientmr/insurance.service';
import type {
  InsuranceSummary,
  InsuranceSettings,
  LogVerificationRequest,
  PostalAddress
} from '../../dtos/patientmr/insurance.dto';
import {
  validateMBI,
  validateMedicaidID,
  validatePhoneNumber,
  formatPhoneNumber,
  validateRequiredFields,
  isLegacyMedicaidId,
  MCO_REQUIRED_PROGRAMS
} from '../../utils/insurance-validation';
import { useHasAnyPermission } from '../../hooks/usePermission';
import { useAuth } from '../../context/authcontext';
import { toAgencyDate, resolveAgencyTimezone } from '../../utils/agencyTime';
import TEXAS_COUNTIES from '../../constants/texas-counties';
import { US_STATE_OPTIONS, normalizeStateCode } from '../../constants/usStates';
import LogVerificationDialog from './insurance/LogVerificationDialog';
import TerminateCoverageDialog from './insurance/TerminateCoverageDialog';
import { RELATIONSHIP_OPTIONS, normalizeRelationship, lacksActivePrimary } from './insurance/insuranceDisplay';

/** Label for a coverage in dialogs and messages (no member IDs). */
const coverageLabel = (c: InsuranceCoverage): string =>
  `${c.cobOrder} ${c.coverageType}${c.payerName && !['MEDICAID', 'MEDICARE'].includes(c.payerName) ? `: ${c.payerName}` : ''}`;

const isEnded = (c: InsuranceCoverage): boolean =>
  c.status === CoverageStatus.EXPIRED || c.status === CoverageStatus.TERMINATED;

interface InsuranceProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId?: string;
}

const Insurance: React.FC<InsuranceProps> = ({ expanded, toggleExpanded, patientId }) => {
  // Permission checks for insurance
  const canCreateInsurance = useHasAnyPermission([
    'patientmr:phi:insurance:create' as any,
    'patientmr:phi:insurance:createall' as any
  ]);

  const canReadInsurance = useHasAnyPermission([
    'patientmr:phi:insurance:read' as any,
    'patientmr:phi:insurance:readall' as any
  ]);

  const canUpdateInsurance = useHasAnyPermission([
    'patientmr:phi:insurance:update' as any,
    'patientmr:phi:insurance:updateall' as any
  ]);

  const canVerifyInsurance = useHasAnyPermission([
    'patientmr:phi:insurance:verify' as any,
    'patientmr:phi:insurance:verifyall' as any
  ]);

  // Read-only mode: has read but no write permissions (coverage is never deleted; it is terminated)
  const isReadOnlyView = canReadInsurance && !canCreateInsurance && !canUpdateInsurance;

  const { agencyTimezone } = useAuth();
  const today = useMemo(() => toAgencyDate(new Date(), resolveAgencyTimezone(agencyTimezone)), [agencyTimezone]);

  const [coverages, setCoverages] = useState<InsuranceCoverage[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  
  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCoverage, setEditingCoverage] = useState<InsuranceCoverage | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [showErrorSummary, setShowErrorSummary] = useState(false);

  // Phase 7: patient summary (badges, coverage-gap banner), payer settings (MCO list), dialogs
  const [summary, setSummary] = useState<InsuranceSummary | null>(null);
  const [settings, setSettings] = useState<InsuranceSettings | null>(null);
  const [terminateTarget, setTerminateTarget] = useState<InsuranceCoverage | null>(null);
  const [verifyTarget, setVerifyTarget] = useState<InsuranceCoverage | null>(null);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const hasLoadedCoveragesRef = useRef<string | null>(null);

  // Ref to track editingCoverage for stable callbacks (avoids dependency churn)
  const editingCoverageRef = useRef(editingCoverage);
  editingCoverageRef.current = editingCoverage;

  // Load coverages from API
  useEffect(() => {
    const fetchCoverages = async () => {
      if (!patientId) return;

      // Prevent duplicate loads for the same patient
      if (hasLoadedCoveragesRef.current === patientId) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await InsuranceService.listCoverages(patientId);
        setCoverages(response.coverages || []);
        setSummary(response.insuranceSummary || null);
        if (response.settings) setSettings(response.settings);
        setHasLoadedOnce(true);

        hasLoadedCoveragesRef.current = patientId;
      } catch (err: any) {
        console.error('Error loading insurance coverages:', err);
        setError(err.message || 'Failed to load insurance coverages. Please try again.');
        setHasLoadedOnce(true);
        hasLoadedCoveragesRef.current = patientId;
      } finally {
        setLoading(false);
      }
    };

    if (expanded && patientId) {
      fetchCoverages();
    }

    if (!expanded) {
      setShowAddForm(false);
    }
  }, [expanded, patientId]);

  // Removed problematic useEffect hooks that caused re-render loops
  // Phone sync is now handled in handleInputChange for better performance

  // Refresh coverages
  const handleRefresh = useCallback(async () => {
    if (!patientId) return;

    try {
      setRefreshing(true);
      setError(null);

      hasLoadedCoveragesRef.current = null;
      const response = await InsuranceService.listCoverages(patientId);
      setCoverages(response.coverages || []);
      setSummary(response.insuranceSummary || null);
      if (response.settings) setSettings(response.settings);
    } catch (err: any) {
      console.error('Error refreshing insurance coverages:', err);
      setError(err.message || 'Failed to refresh insurance coverages.');
    } finally {
      setRefreshing(false);
    }
  }, [patientId]);

  // Open add form with selected payer type
  const handleSelectPayerType = useCallback((payerType: CoverageType) => {
    // Auto-set payerName for Medicare/Medicaid; private and other payers are typed in
    const payerName = payerType === CoverageType.MEDICARE ? 'MEDICARE'
      : payerType === CoverageType.MEDICAID ? 'MEDICAID'
      : '';

    const newCoverage: InsuranceCoverage = {
      coverageType: payerType,
      payerName,
      memberId: '',
      effectiveDate: '',
      status: CoverageStatus.ACTIVE,
      cobOrder: COBOrder.PRIMARY,
      inNetwork: true,
      referralRequired: false,
      paRequired: false
    };

    // Set defaults based on coverage type
    if (payerType === CoverageType.PRIVATE) {
      newCoverage.planType = 'Unknown';
      newCoverage.requiresPriorAuth = 'Unknown';
      // Pediatric default: the policyholder is usually a parent (CMS-1500 box 6)
      newCoverage.relationshipToSubscriber = 'CHILD';
    } else if (payerType === CoverageType.OTHER) {
      newCoverage.relationshipToSubscriber = 'SELF';
    } else if (payerType === CoverageType.MEDICAID) {
      // State of eligibility: blank means the agency's state (set by the server)
      newCoverage.stateOfEligibility = '';
      newCoverage.eligibilityCategory = 'Unknown';
    }

    setEditingCoverage(newCoverage);
    setShowAddForm(true);
  }, []);

  // Open edit form
  const openEditForm = useCallback((id: string | number) => {
    const coverage = coverages.find(c => (c.id === id || c.coverageId === id));
    if (coverage) {
      const edited = { ...coverage };
      // Ensure payerName is set for Medicare/Medicaid (auto-filled by system)
      if (edited.coverageType === CoverageType.MEDICARE && (!edited.payerName || edited.payerName.trim() === '')) {
        edited.payerName = 'MEDICARE';
      } else if (edited.coverageType === CoverageType.MEDICAID && (!edited.payerName || edited.payerName.trim() === '')) {
        edited.payerName = 'MEDICAID';
      }
      // Legacy labels read as codes ('Self' → SELF, 'Texas' → TX)
      if (edited.relationshipToSubscriber) edited.relationshipToSubscriber = normalizeRelationship(edited.relationshipToSubscriber);
      if (edited.stateOfEligibility) edited.stateOfEligibility = normalizeStateCode(edited.stateOfEligibility);
      setEditingCoverage(edited);
      setShowAddForm(true);
    }
  }, [coverages]);

  // Cancel form
  const cancelForm = useCallback(() => {
    setShowAddForm(false);
    setEditingCoverage(null);
    setError(null);
    setValidationErrors({});
    setShowErrorSummary(false);
  }, []);

  // Save coverage (uses ref to avoid re-creating on every editingCoverage change)
  const handleSave = useCallback(async () => {
    const ec = editingCoverageRef.current;
    if (!ec || !patientId) return;

    try {
      // Ensure payerName is set for Medicare/Medicaid before validation
      const payerName = ec.coverageType === CoverageType.MEDICARE ? 'MEDICARE'
        : ec.coverageType === CoverageType.MEDICAID ? 'MEDICAID'
        : ec.payerName;

      const coverageToValidate = { ...ec, payerName };

      // D4: a stored legacy TX- Medicaid ID may stay until someone edits it
      const original = coverages.find(c => (c.coverageId || c.id) === (ec.coverageId || ec.id));
      const legacyIdUnchanged = Boolean(original?.medicaidIdLegacy) && original?.medicaidIdNumber === ec.medicaidIdNumber;

      // Run comprehensive validation
      const errors = validateRequiredFields(coverageToValidate, ec.coverageType, { legacyIdUnchanged });

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setShowErrorSummary(true);
        // Scroll to top to show error summary
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      setLoading(true);
      setError(null);
      setShowErrorSummary(false);

      // Auto-map memberId and effectiveDate for Medicare/Medicaid
      const memberId = ec.coverageType === CoverageType.MEDICARE
        ? (ec.medicareMBI || ec.memberId)
        : ec.coverageType === CoverageType.MEDICAID
        ? (ec.medicaidIdNumber || ec.memberId)
        : ec.memberId;

      const effectiveDate = ec.coverageType === CoverageType.MEDICARE
        ? (ec.effectiveDate || ec.partAEffectiveDate || '')
        : ec.effectiveDate;

      const coverageData: CreateInsuranceCoverageRequest = {
        coverageType: ec.coverageType,
        payerName,
        // An untouched legacy ID is not sent, so the server keeps the stored value
        memberId: legacyIdUnchanged ? (undefined as unknown as string) : memberId,
        effectiveDate,
        terminationDate: ec.terminationDate,
        cobOrder: ec.cobOrder,
        memberName: ec.memberName,
        memberDOB: ec.memberDOB,
        relationshipToSubscriber: ec.relationshipToSubscriber,
        subscriberName: ec.subscriberName,
        subscriberDOB: ec.subscriberDOB,
        subscriberSex: ec.subscriberSex,
        subscriberAddress: ec.subscriberAddress,
        employerName: ec.employerName,
        groupNumber: ec.groupNumber,
        planType: ec.planType,
        claimsPhone: ec.claimsPhone,
        priorAuthPhone: ec.priorAuthPhone,
        requiresPriorAuth: ec.requiresPriorAuth,
        medicareMBI: ec.medicareMBI,
        medicareClaimNumber: ec.medicareClaimNumber,
        partAEffectiveDate: ec.partAEffectiveDate,
        partATerminationDate: ec.partATerminationDate,
        partBEffectiveDate: ec.partBEffectiveDate,
        partBTerminationDate: ec.partBTerminationDate,
        hasMedicareAdvantage: ec.hasMedicareAdvantage,
        maPlanName: ec.maPlanName,
        maPlanID: ec.maPlanID,
        maPlanType: ec.maPlanType,
        maCustomerServicePhone: ec.maCustomerServicePhone,
        maPriorAuthPhone: ec.maPriorAuthPhone,
        hasPartD: ec.hasPartD,
        partDPlanName: ec.partDPlanName,
        partDPlanID: ec.partDPlanID,
        partDEffectiveDate: ec.partDEffectiveDate,
        hasMedigap: ec.hasMedigap,
        supplementalPlanName: ec.supplementalPlanName,
        supplementalPolicyNumber: ec.supplementalPolicyNumber,
        supplementalPhone: ec.supplementalPhone,
        medicaidProgram: ec.medicaidProgram,
        medicaidMCO: ec.medicaidMCO,
        medicaidMCOOther: ec.medicaidMCO === 'OTHER' ? ec.medicaidMCOOther : undefined,
        medicaidType: ec.medicaidType,
        medicaidIdNumber: legacyIdUnchanged ? undefined : ec.medicaidIdNumber,
        mcoMemberId: ec.mcoMemberId,
        mcoCustomerServicePhone: ec.mcoCustomerServicePhone,
        mcoPriorAuthPhone: ec.mcoPriorAuthPhone,
        stateOfEligibility: ec.stateOfEligibility,
        countyOfEligibility: ec.countyOfEligibility,
        eligibilityCategory: ec.eligibilityCategory,
        caseManagerName: ec.caseManagerName,
        caseManagerPhone: ec.caseManagerPhone,
        inNetwork: ec.inNetwork,
        referralRequired: ec.referralRequired,
        paRequired: ec.paRequired,
        pcpName: ec.pcpName,
        pcpNPI: ec.pcpNPI,
        notes: ec.notes
      };

      if (ec.coverageId || ec.id) {
        // Update existing
        const coverageId = (ec.coverageId || ec.id) as string;
        await InsuranceService.updateCoverage(patientId, coverageId, coverageData);
      } else {
        // Create new
        await InsuranceService.createCoverage(patientId, coverageData);
      }

      // Refresh the list
      hasLoadedCoveragesRef.current = null;
      const response = await InsuranceService.listCoverages(patientId);
      setCoverages(response.coverages || []);
      setSummary(response.insuranceSummary || null);
      if (response.settings) setSettings(response.settings);

      cancelForm();
    } catch (err: any) {
      console.error('Error saving insurance coverage:', err);
      setError(err.message || 'Failed to save insurance coverage. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [patientId, cancelForm, coverages]);

  const reloadCoverages = useCallback(async () => {
    if (!patientId) return;
    hasLoadedCoveragesRef.current = null;
    const response = await InsuranceService.listCoverages(patientId);
    setCoverages(response.coverages || []);
    setSummary(response.insuranceSummary || null);
    if (response.settings) setSettings(response.settings);
  }, [patientId]);

  const closeDialogs = useCallback(() => {
    setTerminateTarget(null);
    setVerifyTarget(null);
    setDialogError(null);
  }, []);

  // Terminate coverage: set the last covered date (coverage is never deleted)
  const handleTerminate = useCallback(async (terminationDate: string) => {
    const target = terminateTarget;
    if (!patientId || !target) return;
    try {
      setDialogBusy(true);
      setDialogError(null);
      await InsuranceService.terminateCoverage(patientId, String(target.coverageId || target.id), terminationDate);
      await reloadCoverages();
      closeDialogs();
    } catch (err: any) {
      setDialogError(err.message || 'Failed to terminate coverage.');
    } finally {
      setDialogBusy(false);
    }
  }, [patientId, terminateTarget, reloadCoverages, closeDialogs]);

  // Log a manual eligibility verification (REQ-INS-10)
  const handleLogVerification = useCallback(async (entry: LogVerificationRequest) => {
    const target = verifyTarget;
    if (!patientId || !target) return;
    try {
      setDialogBusy(true);
      setDialogError(null);
      const updated = await InsuranceService.logVerification(patientId, String(target.coverageId || target.id), entry);
      await reloadCoverages();
      setEditingCoverage(prev => (prev && (prev.coverageId || prev.id) === (updated.coverageId || updated.id)
        ? { ...prev, verificationLog: updated.verificationLog, lastVerifiedAt: updated.lastVerifiedAt, verificationDue: updated.verificationDue }
        : prev));
      closeDialogs();
    } catch (err: any) {
      setDialogError(err.message || 'Failed to log verification.');
    } finally {
      setDialogBusy(false);
    }
  }, [patientId, verifyTarget, reloadCoverages, closeDialogs]);

  const setSubscriberAddress = useCallback((field: keyof PostalAddress, value: string) => {
    setEditingCoverage(prev => prev ? { ...prev, subscriberAddress: { ...(prev.subscriberAddress || {}), [field]: value } } : null);
  }, []);

  // Handle input change
  const handleInputChange = useCallback((field: keyof InsuranceCoverage, value: any) => {
    // Auto-format phone numbers
    let formattedValue = value;
    if (field.toString().includes('Phone') && typeof value === 'string') {
      formattedValue = formatPhoneNumber(value);
    }
    
    setEditingCoverage(prev => {
      if (!prev) return null;
      
      const updated = { ...prev, [field]: formattedValue };
      
      // Handle "Same As" phone sync automatically
      if (field === 'maCustomerServicePhone' && prev.maPriorAuthPhone === prev.maCustomerServicePhone) {
        updated.maPriorAuthPhone = formattedValue;
      }
      if (field === 'mcoCustomerServicePhone' && prev.mcoPriorAuthPhone === prev.mcoCustomerServicePhone) {
        updated.mcoPriorAuthPhone = formattedValue;
      }
      
      return updated;
    });
    
    // Clear validation error when user starts typing
    setValidationErrors(prev => {
      if (!prev[field]) return prev; // Avoid state update if no error exists
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []); // Removed dependencies since we use functional updates

  // Validate individual field (uses ref for stable identity)
  const validateField = useCallback((fieldName: string) => {
    const ec = editingCoverageRef.current;
    if (!ec) return;

    const value = ec[fieldName as keyof InsuranceCoverage];
    let error = '';

    if (fieldName === 'medicareMBI' && ec.coverageType === CoverageType.MEDICARE) {
      const mbiValidation = validateMBI(value as string);
      if (!mbiValidation.isValid) {
        error = mbiValidation.error;
      }
    } else if (fieldName === 'medicaidIdNumber' && ec.coverageType === CoverageType.MEDICAID && !isLegacyMedicaidId(`TX-${value}`)) {
      const idValidation = validateMedicaidID(value as string);
      if (!idValidation.isValid) {
        error = idValidation.error;
      }
    } else if (fieldName.includes('Phone') && value) {
      const phoneValidation = validatePhoneNumber(value as string);
      if (!phoneValidation.isValid) {
        error = phoneValidation.error;
      }
    }

    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  }, []);

  // Handle conditional field changes
  const handleMedicareAdvantageChange = useCallback((hasMA: boolean) => {
    setEditingCoverage(prev => prev ? ({
      ...prev,
      hasMedicareAdvantage: hasMA,
      // Clear fields if disabled
      ...(!hasMA && {
        maPlanName: '',
        maPlanID: '',
        maPlanType: '',
        maCustomerServicePhone: '',
        maPriorAuthPhone: ''
      })
    }) : null);
  }, []);

  const handlePartDChange = useCallback((hasPartD: boolean) => {
    setEditingCoverage(prev => prev ? ({
      ...prev,
      hasPartD: hasPartD,
      // Clear fields if disabled
      ...(!hasPartD && {
        partDPlanName: '',
        partDPlanID: '',
        partDEffectiveDate: ''
      })
    }) : null);
  }, []);

  const handleMedigapChange = useCallback((hasMedigap: boolean) => {
    setEditingCoverage(prev => prev ? ({
      ...prev,
      hasMedigap: hasMedigap,
      // Clear fields if disabled
      ...(!hasMedigap && {
        supplementalPlanName: '',
        supplementalPolicyNumber: '',
        supplementalPhone: ''
      })
    }) : null);
  }, []);

  // Get payer type color
  const getPayerTypeColor = useCallback((type: CoverageType): string => {
    switch (type) {
      case CoverageType.PRIVATE:
        return 'bg-blue-100 text-blue-800';
      case CoverageType.MEDICAID:
        return 'bg-green-100 text-green-800';
      case CoverageType.MEDICARE:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }, []);

  // Format date
  const formatDate = useCallback((dateString: string): string => {
    return InsuranceService.formatDate(dateString);
  }, []);

  // Define columns for desktop list view
  const columns = useMemo(() => [
    {
      key: 'payerInfo',
      label: 'Payer Information',
      width: 'flex-1',
      render: (coverage: InsuranceCoverage) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {coverage.cobOrder === COBOrder.PRIMARY && (
              <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-medium rounded">
                PRIMARY
              </span>
            )}
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${getPayerTypeColor(coverage.coverageType)}`}>
              {coverage.coverageType}
            </span>
          </div>
          <div className="font-medium text-slate-900">{coverage.payerName}</div>
        </div>
      )
    },
    {
      key: 'memberId',
      label: 'Member ID',
      width: 'w-40',
      render: (coverage: InsuranceCoverage) => (
        <div className="flex flex-col">
          <span className="font-medium">{coverage.memberId}</span>
          {coverage.medicaidIdLegacy && (
            <span className="mt-0.5 w-fit px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" title="Re-enter the 9-digit Medicaid ID">
              Legacy ID format
            </span>
          )}
          {coverage.coverageType === CoverageType.MEDICARE && coverage.medicareMBI && (
            <span className="text-sm text-slate-600">MBI: {coverage.medicareMBI}</span>
          )}
        </div>
      )
    },
    {
      key: 'verification',
      label: 'Last Verified',
      width: 'w-32',
      render: (coverage: InsuranceCoverage) => (
        <span
          className={`text-sm ${coverage.verificationDue ? 'px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium' : 'text-slate-700'}`}
          title={coverage.verificationDue ? 'Medicaid eligibility can change monthly: verify again' : undefined}
        >
          {coverage.lastVerifiedAt ? formatDate(coverage.lastVerifiedAt) : 'Never'}
        </span>
      )
    },
    {
      key: 'effectiveDate',
      label: 'Effective Date',
      width: 'w-32',
      render: (coverage: InsuranceCoverage) => (
        <div className="flex flex-col text-sm">
          <span>{formatDate(coverage.effectiveDate)}</span>
          {coverage.terminationDate && (
            <span className="text-red-600">to {formatDate(coverage.terminationDate)}</span>
          )}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: 'w-24',
      align: 'center' as const,
      render: (coverage: InsuranceCoverage) => (
        <StatusPill status={coverage.status} />
      )
    }
  ], [formatDate, getPayerTypeColor]);

  // Define actions
  const actions = useMemo(() => {
    const actionsList = [];
    
    // View/Edit button (always show if canRead)
    if (canReadInsurance) {
      actionsList.push({
        icon: <Edit size={14} />,
        label: isReadOnlyView ? 'View coverage' : 'Edit coverage',
        onClick: (coverage: InsuranceCoverage) => openEditForm(coverage.coverageId || coverage.id),
        className: isReadOnlyView
          ? 'p-1.5 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors'
          : 'p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors'
      });
    }
    
    // Log a manual verification (REQ-INS-10)
    if (canVerifyInsurance) {
      actionsList.push({
        icon: <ClipboardCheck size={14} />,
        label: 'Log verification',
        onClick: (coverage: InsuranceCoverage) => { setDialogError(null); setVerifyTarget(coverage); },
        className: 'p-1.5 text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors'
      });
    }

    // Terminate (set the last covered date); coverage is never deleted
    if (canUpdateInsurance) {
      actionsList.push({
        icon: <CalendarX size={14} />,
        label: 'Terminate coverage',
        onClick: (coverage: InsuranceCoverage) => { setDialogError(null); setTerminateTarget(coverage); },
        className: 'p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors',
        isVisible: (coverage: InsuranceCoverage) => !isEnded(coverage)
      });
    }

    return actionsList;
  }, [canReadInsurance, canVerifyInsurance, canUpdateInsurance, isReadOnlyView, openEditForm]);

  // Mobile card renderer
  const mobileCard = useMemo(() => ({
    primaryText: (coverage: InsuranceCoverage) => coverage.payerName,
    secondaryText: (coverage: InsuranceCoverage) => `${coverage.coverageType} - Member ID: ${coverage.memberId}`,
    tertiaryText: (coverage: InsuranceCoverage) => `Member ID: ${coverage.memberId}`,
    topRightContent: (coverage: InsuranceCoverage) => {
      const isActive = InsuranceService.isCoverageActive(coverage);
      return <Shield className={`h-5 w-5 ${isActive ? 'text-green-600' : 'text-gray-400'}`} />;
    },
    badges: (coverage: InsuranceCoverage) => [
      ...(coverage.cobOrder === COBOrder.PRIMARY ? [{
        label: 'PRIMARY',
        className: 'bg-blue-600 text-white'
      }] : []),
      {
        label: coverage.coverageType,
        className: getPayerTypeColor(coverage.coverageType)
      },
      {
        label: coverage.status,
        className: getStatusPillClassName(coverage.status)
      }
    ],
    footer: (coverage: InsuranceCoverage) => (
      <div className="space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Effective:</span>
          <span className="font-medium">{formatDate(coverage.effectiveDate)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Last verified:</span>
          <span className={`font-medium ${coverage.verificationDue ? 'text-amber-700' : ''}`}>
            {coverage.lastVerifiedAt ? formatDate(coverage.lastVerifiedAt) : 'Never'}
          </span>
        </div>
        {coverage.terminationDate && (
          <div className="flex justify-between">
            <span className="text-gray-600">Termination:</span>
            <span className="font-medium text-red-600">{formatDate(coverage.terminationDate)}</span>
          </div>
        )}
      </div>
    ),
    actions: (coverage: InsuranceCoverage) => {
      const actionsList = [];
      
      // View/Edit button
      if (canReadInsurance) {
        actionsList.push({
          icon: <Edit size={18} />,
          label: isReadOnlyView ? 'View' : 'Edit',
          onClick: () => openEditForm(coverage.coverageId || coverage.id)
        });
      }
      
      if (canVerifyInsurance) {
        actionsList.push({
          icon: <ClipboardCheck size={18} />,
          label: 'Log verification',
          onClick: () => { setDialogError(null); setVerifyTarget(coverage); }
        });
      }

      if (canUpdateInsurance && !isEnded(coverage)) {
        actionsList.push({
          icon: <CalendarX size={18} />,
          label: 'Terminate',
          onClick: () => { setDialogError(null); setTerminateTarget(coverage); }
        });
      }

      return actionsList;
    }
  }), [formatDate, getPayerTypeColor, openEditForm, canReadInsurance, canVerifyInsurance, canUpdateInsurance, isReadOnlyView]);

  // Alert banner for no patient selected
  const alertBanner = useMemo(() => {
    if (!patientId) {
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-amber-600 mr-2 mt-0.5" />
            <p className="text-sm text-amber-700">
              Please select a patient to view and manage insurance coverage.
            </p>
          </div>
        </div>
      );
    }
    if (!hasLoadedOnce) return undefined;
    return (
      <div className="space-y-2">
        {lacksActivePrimary(summary) && (
          <Banner variant="error" title="No active primary coverage" data-testid="no-active-primary-banner">
            No primary coverage is in effect today. Visits can't be billed until one is added.
          </Banner>
        )}
        {/* REQ-INS-09: Pelora never checks eligibility itself */}
        <p className="text-xs text-slate-500">
          Verify eligibility manually in the TMHP or payer portal (or by phone or fax), then use Log verification to record it.
        </p>
      </div>
    );
  }, [patientId, hasLoadedOnce, summary]);

  // Key extractor for list items
  const keyExtractor = useCallback((coverage: InsuranceCoverage) => 
    String(coverage.coverageId || coverage.id), []);

  // Empty state icon
  const emptyStateIcon = useMemo(() => (
    <Shield className="text-gray-300" size={48} />
  ), []);

  // Memoize 254 Texas county options to avoid recreating on every render
  const texasCountyOptions = useMemo(() =>
    TEXAS_COUNTIES.map(county => (
      <option key={county} value={county}>{county}</option>
    )), []);

  // Memoize form JSX output (useMemo, not useCallback) to prevent reconciliation on parent re-renders
  const formContent = useMemo(() => {
    if (!editingCoverage) return null;

    // Case C: User lacks read permissions - prevent form access
    if (!canReadInsurance) {
      return (
        <FormPageLayout
          title="Edit Insurance Coverage"
          onBack={cancelForm}
          actions={[]}
        >
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-center max-w-md">
              <div className="text-red-500 text-4xl mb-4">🔒</div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Not enough permissions</h3>
              <p className="text-gray-600 text-sm">
                You don't have the required permissions to view insurance information. Contact your administrator if you need access.
              </p>
            </div>
          </div>
        </FormPageLayout>
      );
    }

    const formTitle = editingCoverage.id || editingCoverage.coverageId
      ? `Edit ${editingCoverage.coverageType} Insurance`
      : `Add ${editingCoverage.coverageType} Insurance`;

    const formActions: ActionButtonConfig[] = [
      {
        key: 'cancel',
        label: 'Cancel',
        variant: 'secondary',
        onClick: cancelForm,
        disabled: loading,
      },
      {
        key: 'save',
        label: 'Save Coverage',
        loadingLabel: 'Saving...',
        variant: 'primary' as const,
        onClick: handleSave,
        loading: loading,
        disabled: loading,
        // Hide save button in read-only mode
        hidden: isReadOnlyView,
      },
    ];

    return (
      <FormPageLayout title={formTitle} onBack={cancelForm} actions={formActions}>
        {/* Read-only mode notification */}
        {isReadOnlyView && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              <strong>View Only:</strong> You have permission to view this insurance coverage, but cannot make changes.
            </p>
          </div>
        )}

        {/* Error Summary */}
        {showErrorSummary && Object.keys(validationErrors).length > 0 && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-800">
                  Please correct the following errors:
                </h3>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                  {Object.entries(validationErrors).map(([field, error]) => (
                    <li key={field}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Form Fields */}
        <div className="space-y-6">
          {/* PRIVATE AND OTHER PAYER FORM */}
          {(editingCoverage.coverageType === CoverageType.PRIVATE || editingCoverage.coverageType === CoverageType.OTHER) && (
            <>
              {/* Insurance Company / Payer Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                  {editingCoverage.coverageType === CoverageType.OTHER ? 'Payer Name' : 'Insurance Company Name'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingCoverage.payerName || ''}
                  onChange={(e) => handleInputChange('payerName', e.target.value)}
                  disabled={isReadOnlyView}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                    validationErrors.payerName ? 'border-red-500' : 'border-slate-300'
                  } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  placeholder="e.g., Blue Cross Blue Shield"
                />
                {validationErrors.payerName && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.payerName}</p>
                )}
              </div>

              {/* Policy/Member ID and Group Number */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Policy/Member ID {editingCoverage.coverageType === CoverageType.PRIVATE && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="text"
                    value={editingCoverage.memberId || ''}
                    onChange={(e) => handleInputChange('memberId', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                    placeholder="Enter policy/member ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Group Number
                  </label>
                  <input
                    type="text"
                    value={editingCoverage.groupNumber || ''}
                    onChange={(e) => handleInputChange('groupNumber', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                    placeholder="Enter group number"
                  />
                </div>
              </div>

              {/* Plan Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Plan Type
                  </label>
                  <select
                    value={editingCoverage.planType || 'Unknown'}
                    onChange={(e) => handleInputChange('planType', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  >
                    <option value="Unknown">Unknown</option>
                    <option value="PPO">PPO</option>
                    <option value="HMO">HMO</option>
                    <option value="EPO">EPO</option>
                    <option value="POS">POS</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Requires Prior Authorization (private insurance) */}
              {editingCoverage.coverageType === CoverageType.PRIVATE && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 text-left">
                  Requires Prior Authorization? <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      checked={editingCoverage.requiresPriorAuth === 'Yes'}
                      onChange={() => handleInputChange('requiresPriorAuth', 'Yes')}
                      disabled={isReadOnlyView}
                      className={`form-radio h-4 w-4 text-blue-600 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <span className="ml-2 text-sm text-slate-700">Yes</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      checked={editingCoverage.requiresPriorAuth === 'No'}
                      onChange={() => handleInputChange('requiresPriorAuth', 'No')}
                      disabled={isReadOnlyView}
                      className={`form-radio h-4 w-4 text-blue-600 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <span className="ml-2 text-sm text-slate-700">No</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      checked={editingCoverage.requiresPriorAuth === 'Unknown'}
                      onChange={() => handleInputChange('requiresPriorAuth', 'Unknown')}
                      disabled={isReadOnlyView}
                      className={`form-radio h-4 w-4 text-blue-600 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <span className="ml-2 text-sm text-slate-700">Unknown</span>
                  </label>
                </div>
              </div>
              )}

              {/* Effective and Termination Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Effective Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={editingCoverage.effectiveDate || ''}
                    onChange={(e) => handleInputChange('effectiveDate', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Termination Date
                  </label>
                  <input
                    type="date"
                    value={editingCoverage.terminationDate || ''}
                    onChange={(e) => handleInputChange('terminationDate', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
              </div>

              {/* Policy Holder Section (CMS-1500 boxes 4, 6, 7, 11) */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="text-md font-semibold text-slate-900 mb-4">Policyholder</h4>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                      Patient's relationship to the policyholder <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={editingCoverage.relationshipToSubscriber || 'SELF'}
                      onChange={(e) => handleInputChange('relationshipToSubscriber', e.target.value)}
                      disabled={isReadOnlyView}
                      className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                    >
                      {RELATIONSHIP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>

                  {(editingCoverage.relationshipToSubscriber || 'SELF') !== 'SELF' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                            Policyholder Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={editingCoverage.subscriberName || ''}
                            onChange={(e) => handleInputChange('subscriberName', e.target.value)}
                            disabled={isReadOnlyView}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${validationErrors.subscriberName ? 'border-red-500' : 'border-slate-300'} ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                            placeholder="e.g. the patient's mother"
                          />
                          {validationErrors.subscriberName && <p className="mt-1 text-sm text-red-600">{validationErrors.subscriberName}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1 text-left">Sex</label>
                          <select
                            value={editingCoverage.subscriberSex || ''}
                            onChange={(e) => handleInputChange('subscriberSex', e.target.value || undefined)}
                            disabled={isReadOnlyView}
                            className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          >
                            <option value="">Not recorded</option>
                            <option value="F">Female</option>
                            <option value="M">Male</option>
                            <option value="U">Unknown</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          Policyholder Date of Birth <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={editingCoverage.subscriberDOB || ''}
                          onChange={(e) => handleInputChange('subscriberDOB', e.target.value)}
                          disabled={isReadOnlyView}
                          className={`w-full md:w-1/2 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${validationErrors.subscriberDOB ? 'border-red-500' : 'border-slate-300'} ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        />
                        {validationErrors.subscriberDOB && <p className="mt-1 text-sm text-red-600">{validationErrors.subscriberDOB}</p>}
                      </div>
                      <fieldset className="space-y-2">
                        <legend className="block text-sm font-medium text-slate-700 text-left">Policyholder Address</legend>
                        <input
                          type="text"
                          aria-label="Policyholder street address"
                          value={editingCoverage.subscriberAddress?.line1 || ''}
                          onChange={(e) => setSubscriberAddress('line1', e.target.value)}
                          disabled={isReadOnlyView}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md text-base"
                          placeholder="Street address"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          <input
                            type="text"
                            aria-label="Policyholder city"
                            value={editingCoverage.subscriberAddress?.city || ''}
                            onChange={(e) => setSubscriberAddress('city', e.target.value)}
                            disabled={isReadOnlyView}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md text-base"
                            placeholder="City"
                          />
                          <select
                            aria-label="Policyholder state"
                            value={editingCoverage.subscriberAddress?.state || ''}
                            onChange={(e) => setSubscriberAddress('state', e.target.value)}
                            disabled={isReadOnlyView}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md text-base"
                          >
                            <option value="">State</option>
                            {US_STATE_OPTIONS.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
                          </select>
                          <input
                            type="text"
                            aria-label="Policyholder ZIP code"
                            value={editingCoverage.subscriberAddress?.zip || ''}
                            onChange={(e) => setSubscriberAddress('zip', e.target.value)}
                            disabled={isReadOnlyView}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md text-base"
                            placeholder="ZIP"
                            maxLength={10}
                          />
                        </div>
                      </fieldset>
                    </>
                  )}
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="text-md font-semibold text-slate-900 mb-4">Contact Information</h4>
                
                <div className="space-y-4">
                  {/* Claims Phone and Prior Auth Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                        Claims Phone
                      </label>
                      <input
                        type="tel"
                        value={editingCoverage.claimsPhone || ''}
                        onChange={(e) => handleInputChange('claimsPhone', e.target.value)}
                        disabled={isReadOnlyView}
                        className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        placeholder="XXX-XXX-XXXX"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                        Prior Auth Phone
                      </label>
                      <input
                        type="tel"
                        value={editingCoverage.priorAuthPhone || ''}
                        onChange={(e) => handleInputChange('priorAuthPhone', e.target.value)}
                        disabled={isReadOnlyView}
                        className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        placeholder="XXX-XXX-XXXX"
                      />
                    </div>
                  </div>

                  {/* Same as Claims Phone Checkbox */}
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={editingCoverage.priorAuthPhone === editingCoverage.claimsPhone && !!editingCoverage.claimsPhone}
                        onChange={(e) => {
                          if (e.target.checked && editingCoverage.claimsPhone) {
                            handleInputChange('priorAuthPhone', editingCoverage.claimsPhone);
                          }
                        }}
                        disabled={isReadOnlyView}
                        className={`w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                      />
                      <span className="text-sm text-slate-700">Same as Claims Phone</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Coverage Section */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="text-md font-semibold text-slate-900 mb-4">Coverage</h4>
                
                <div className="space-y-4">
                  {/* Is this Primary Insurance */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 text-left">
                      Is this Primary Insurance? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-6">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="isPrimary"
                          checked={editingCoverage.cobOrder === COBOrder.PRIMARY}
                          onChange={() => handleInputChange('cobOrder', COBOrder.PRIMARY)}
                          disabled={isReadOnlyView}
                          className={`w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                        />
                        <span className="text-sm text-slate-700">Yes</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="isPrimary"
                          checked={editingCoverage.cobOrder !== COBOrder.PRIMARY}
                          onChange={() => handleInputChange('cobOrder', COBOrder.SECONDARY)}
                          disabled={isReadOnlyView}
                          className={`w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                        />
                        <span className="text-sm text-slate-700">No</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="text-md font-semibold text-slate-900 mb-4">Notes</h4>
                <textarea
                  value={editingCoverage.notes || ''}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  disabled={isReadOnlyView}
                  rows={4}
                  className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  placeholder="Enter any additional notes"
                />
              </div>
            </>
          )}

          {/* MEDICARE FORM */}
          {editingCoverage.coverageType === CoverageType.MEDICARE && (
            <>
              {/* Medicare Beneficiary Identifier (MBI) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                  Medicare Beneficiary Identifier (MBI) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editingCoverage.medicareMBI || ''}
                  onChange={(e) => handleInputChange('medicareMBI', e.target.value)}
                  onBlur={() => validateField('medicareMBI')}
                  disabled={isReadOnlyView}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                    validationErrors.medicareMBI ? 'border-red-500' : 'border-slate-300'
                  } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  placeholder="1EG4-TE5-MK73"
                  maxLength={13}
                />
                {validationErrors.medicareMBI && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.medicareMBI}</p>
                )}
              </div>

              {/* Part A Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Part A Effective Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={editingCoverage.partAEffectiveDate || ''}
                    onChange={(e) => handleInputChange('partAEffectiveDate', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                      validationErrors.partAEffectiveDate ? 'border-red-500' : 'border-slate-300'
                    } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                  {validationErrors.partAEffectiveDate && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.partAEffectiveDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Part A Termination Date
                  </label>
                  <input
                    type="date"
                    value={editingCoverage.partATerminationDate || ''}
                    onChange={(e) => handleInputChange('partATerminationDate', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                      validationErrors.partATerminationDate ? 'border-red-500' : 'border-slate-300'
                    } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                  {validationErrors.partATerminationDate && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.partATerminationDate}</p>
                  )}
                </div>
              </div>

              {/* Part B Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Part B Effective Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={editingCoverage.partBEffectiveDate || ''}
                    onChange={(e) => handleInputChange('partBEffectiveDate', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                      validationErrors.partBEffectiveDate ? 'border-red-500' : 'border-slate-300'
                    } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                  {validationErrors.partBEffectiveDate && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.partBEffectiveDate}</p>
                  )}
                  <p className="mt-1 text-xs text-slate-500">Part B date must be on or after Part A date</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Part B Termination Date
                  </label>
                  <input
                    type="date"
                    value={editingCoverage.partBTerminationDate || ''}
                    onChange={(e) => handleInputChange('partBTerminationDate', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                      validationErrors.partBTerminationDate ? 'border-red-500' : 'border-slate-300'
                    } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                  {validationErrors.partBTerminationDate && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.partBTerminationDate}</p>
                  )}
                </div>
              </div>

              {/* Medicare Advantage Section */}
              <div className="border-t border-slate-200 pt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2 text-left">
                  Has Medicare Advantage Plan? <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-4 mb-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      checked={editingCoverage.hasMedicareAdvantage === true}
                      onChange={() => handleMedicareAdvantageChange(true)}
                      disabled={isReadOnlyView}
                      className={`form-radio h-4 w-4 text-blue-600 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <span className="ml-2 text-sm text-slate-700">Yes</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      checked={editingCoverage.hasMedicareAdvantage === false}
                      onChange={() => handleMedicareAdvantageChange(false)}
                      disabled={isReadOnlyView}
                      className={`form-radio h-4 w-4 text-blue-600 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <span className="ml-2 text-sm text-slate-700">No</span>
                  </label>
                </div>

                {editingCoverage.hasMedicareAdvantage && (
                  <div className="p-4 bg-blue-50 rounded-md space-y-4">
                    <h4 className="font-medium text-gray-900">Medicare Advantage Information</h4>
                    
                    {/* MA Plan Name and Plan ID */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          MA Plan Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={editingCoverage.maPlanName || ''}
                          onChange={(e) => handleInputChange('maPlanName', e.target.value)}
                          disabled={isReadOnlyView}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                            validationErrors.maPlanName ? 'border-red-500' : 'border-slate-300'
                          } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          placeholder="e.g., Humana Gold Plus"
                        />
                        {validationErrors.maPlanName && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.maPlanName}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          MA Plan ID
                        </label>
                        <input
                          type="text"
                          value={editingCoverage.maPlanID || ''}
                          onChange={(e) => handleInputChange('maPlanID', e.target.value)}
                          disabled={isReadOnlyView}
                          className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          placeholder="H1234-001"
                        />
                      </div>
                    </div>

                    {/* MA Plan Type */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                        MA Plan Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={editingCoverage.maPlanType || ''}
                        onChange={(e) => handleInputChange('maPlanType', e.target.value)}
                        disabled={isReadOnlyView}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                          validationErrors.maPlanType ? 'border-red-500' : 'border-slate-300'
                        } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                      >
                        <option value="">Select Plan Type</option>
                        <option value="HMO">HMO</option>
                        <option value="PPO">PPO</option>
                        <option value="PFFS">PFFS (Private Fee-for-Service)</option>
                        <option value="SNP">SNP (Special Needs Plan)</option>
                        <option value="Other">Other</option>
                      </select>
                      {validationErrors.maPlanType && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.maPlanType}</p>
                      )}
                    </div>

                    {/* MA Customer Service and Prior Auth Phones */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          MA Customer Service Phone
                        </label>
                        <input
                          type="tel"
                          value={editingCoverage.maCustomerServicePhone || ''}
                          onChange={(e) => handleInputChange('maCustomerServicePhone', e.target.value)}
                          onBlur={() => validateField('maCustomerServicePhone')}
                          disabled={isReadOnlyView}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                            validationErrors.maCustomerServicePhone ? 'border-red-500' : 'border-slate-300'
                          } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          placeholder="(XXX) XXX-XXXX"
                        />
                        {validationErrors.maCustomerServicePhone && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.maCustomerServicePhone}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          MA Prior Auth Phone
                        </label>
                        <input
                          type="tel"
                          value={editingCoverage.maPriorAuthPhone || ''}
                          onChange={(e) => handleInputChange('maPriorAuthPhone', e.target.value)}
                          onBlur={() => validateField('maPriorAuthPhone')}
                          disabled={isReadOnlyView}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                            validationErrors.maPriorAuthPhone ? 'border-red-500' : 'border-slate-300'
                          } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          placeholder="(XXX) XXX-XXXX"
                        />
                        {validationErrors.maPriorAuthPhone && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.maPriorAuthPhone}</p>
                        )}
                      </div>
                    </div>

                    {/* Same as Customer Service Checkbox */}
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editingCoverage.maPriorAuthPhone === editingCoverage.maCustomerServicePhone && !!editingCoverage.maCustomerServicePhone}
                          onChange={(e) => {
                            if (e.target.checked && editingCoverage.maCustomerServicePhone) {
                              handleInputChange('maPriorAuthPhone', editingCoverage.maCustomerServicePhone);
                            }
                          }}
                          disabled={isReadOnlyView}
                          className={`w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                        />
                        <span className="text-sm text-slate-700">Same as Customer Service</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Part D Section */}
              <div className="border-t border-slate-200 pt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2 text-left">
                  Has Part D (Prescription Drug) Coverage? <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-4 mb-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      checked={editingCoverage.hasPartD === true}
                      onChange={() => handlePartDChange(true)}
                      disabled={isReadOnlyView}
                      className={`form-radio h-4 w-4 text-blue-600 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <span className="ml-2 text-sm text-slate-700">Yes</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      checked={editingCoverage.hasPartD === false}
                      onChange={() => handlePartDChange(false)}
                      disabled={isReadOnlyView}
                      className={`form-radio h-4 w-4 text-blue-600 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <span className="ml-2 text-sm text-slate-700">No</span>
                  </label>
                </div>

                {editingCoverage.hasPartD && (
                  <div className="p-4 bg-green-50 rounded-md space-y-4">
                    <h4 className="font-medium text-gray-900">Part D Information</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          Part D Plan Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={editingCoverage.partDPlanName || ''}
                          onChange={(e) => handleInputChange('partDPlanName', e.target.value)}
                          disabled={isReadOnlyView}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                            validationErrors.partDPlanName ? 'border-red-500' : 'border-slate-300'
                          } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          placeholder="e.g., SilverScript Choice"
                        />
                        {validationErrors.partDPlanName && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.partDPlanName}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          Part D Plan ID <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={editingCoverage.partDPlanID || ''}
                          onChange={(e) => handleInputChange('partDPlanID', e.target.value)}
                          disabled={isReadOnlyView}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                            validationErrors.partDPlanID ? 'border-red-500' : 'border-slate-300'
                          } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          placeholder="S1234"
                          required
                        />
                        {validationErrors.partDPlanID && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.partDPlanID}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                        Part D Effective Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={editingCoverage.partDEffectiveDate || ''}
                        onChange={(e) => handleInputChange('partDEffectiveDate', e.target.value)}
                        disabled={isReadOnlyView}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                          validationErrors.partDEffectiveDate ? 'border-red-500' : 'border-slate-300'
                        } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                      />
                      {validationErrors.partDEffectiveDate && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.partDEffectiveDate}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Medigap/Supplemental Section */}
              <div className="border-t border-slate-200 pt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2 text-left">
                  Has Medigap/Supplemental Insurance? <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-4 mb-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      checked={editingCoverage.hasMedigap === true}
                      onChange={() => handleMedigapChange(true)}
                      disabled={isReadOnlyView}
                      className={`form-radio h-4 w-4 text-blue-600 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <span className="ml-2 text-sm text-slate-700">Yes</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      checked={editingCoverage.hasMedigap === false}
                      onChange={() => handleMedigapChange(false)}
                      disabled={isReadOnlyView}
                      className={`form-radio h-4 w-4 text-blue-600 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                    <span className="ml-2 text-sm text-slate-700">No</span>
                  </label>
                </div>

                {editingCoverage.hasMedigap && (
                  <div className="p-4 bg-purple-50 rounded-md space-y-4">
                    <h4 className="font-medium text-gray-900">Medigap/Supplemental Insurance Information</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          Supplemental Plan Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={editingCoverage.supplementalPlanName || ''}
                          onChange={(e) => handleInputChange('supplementalPlanName', e.target.value)}
                          disabled={isReadOnlyView}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                            validationErrors.supplementalPlanName ? 'border-red-500' : 'border-slate-300'
                          } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          placeholder="e.g., AARP Medigap Plan G"
                        />
                        {validationErrors.supplementalPlanName && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.supplementalPlanName}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          Supplemental Policy Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={editingCoverage.supplementalPolicyNumber || ''}
                          onChange={(e) => handleInputChange('supplementalPolicyNumber', e.target.value)}
                          disabled={isReadOnlyView}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                            validationErrors.supplementalPolicyNumber ? 'border-red-500' : 'border-slate-300'
                          } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          placeholder="Policy number"
                        />
                        {validationErrors.supplementalPolicyNumber && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.supplementalPolicyNumber}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                        Supplemental Customer Service Phone
                      </label>
                      <input
                        type="tel"
                        value={editingCoverage.supplementalPhone || ''}
                        onChange={(e) => handleInputChange('supplementalPhone', e.target.value)}
                        onBlur={() => validateField('supplementalPhone')}
                        disabled={isReadOnlyView}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                          validationErrors.supplementalPhone ? 'border-red-500' : 'border-slate-300'
                        } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        placeholder="(XXX) XXX-XXXX"
                      />
                      {validationErrors.supplementalPhone && (
                        <p className="mt-1 text-sm text-red-600">{validationErrors.supplementalPhone}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Effective and Termination Dates - Keep for backward compatibility */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 hidden">
                <div>
                  <input
                    type="date"
                    value={editingCoverage.effectiveDate || ''}
                    onChange={(e) => handleInputChange('effectiveDate', e.target.value)}
                  />
                </div>

                <div>
                  <input
                    type="date"
                    value={editingCoverage.terminationDate || ''}
                    onChange={(e) => handleInputChange('terminationDate', e.target.value)}
                  />
                </div>
              </div>

              {/* Additional Information Section */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="text-md font-semibold text-slate-900 mb-4">Additional Information</h4>
                
                <div className="space-y-4">
                  {/* Medicare Claim Number (Legacy) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                      Medicare Claim Number (Legacy)
                    </label>
                    <input
                      type="text"
                      value={editingCoverage.medicareClaimNumber || ''}
                      onChange={(e) => handleInputChange('medicareClaimNumber', e.target.value)}
                      disabled={isReadOnlyView}
                      className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                      placeholder="Old HICN format (if applicable)"
                    />
                    <p className="text-xs text-slate-500 mt-1">(Optional - legacy format)</p>
                  </div>

                  {/* Is Primary Insurance */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 text-left">
                      Is this Primary Insurance? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex space-x-4">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          checked={editingCoverage.cobOrder === COBOrder.PRIMARY}
                          onChange={() => handleInputChange('cobOrder', COBOrder.PRIMARY)}
                          disabled={isReadOnlyView}
                          className={`form-radio h-4 w-4 text-blue-600 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                        />
                        <span className="ml-2 text-sm text-slate-700">Yes</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          checked={editingCoverage.cobOrder !== COBOrder.PRIMARY}
                          onChange={() => handleInputChange('cobOrder', COBOrder.SECONDARY)}
                          disabled={isReadOnlyView}
                          className={`form-radio h-4 w-4 text-blue-600 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                        />
                        <span className="ml-2 text-sm text-slate-700">No</span>
                      </label>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Only one insurance can be primary</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="text-md font-semibold text-slate-900 mb-4">Notes</h4>
                <textarea
                  value={editingCoverage.notes || ''}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  disabled={isReadOnlyView}
                  rows={4}
                  className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  placeholder="Enter any additional notes"
                />
              </div>
            </>
          )}

          {/* MEDICAID FORM */}
          {editingCoverage.coverageType === CoverageType.MEDICAID && (
            <>
              {/* Medicaid Type and ID */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Medicaid Program <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={editingCoverage.medicaidType || ''}
                    onChange={(e) => handleInputChange('medicaidType', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  >
                    <option value="">Select Medicaid Type</option>
                    <option value="FFS">Texas Medicaid Fee-for-Service (FFS)</option>
                    <option value="MCO">Managed Care Organization (MCO)</option>
                    <option value="CHIP">CHIP (Children's Health Insurance Program)</option>
                    <option value="CHIP_PERINATAL">CHIP Perinatal</option>
                    <option value="STAR">STAR (Medicaid Managed Care)</option>
                    <option value="STAR_PLUS">STAR+PLUS (Medicaid + Medicare dual eligible)</option>
                    <option value="STAR_KIDS">STAR Kids (children with disabilities)</option>
                    <option value="STAR_HEALTH">STAR Health (foster care)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Medicaid ID Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editingCoverage.medicaidIdNumber || ''}
                    onChange={(e) => handleInputChange('medicaidIdNumber', e.target.value)}
                    onBlur={() => validateField('medicaidIdNumber')}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                      validationErrors.medicaidIdNumber ? 'border-red-500' : 'border-slate-300'
                    } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                    placeholder="9 digits"
                    maxLength={11}
                  />
                  {editingCoverage.medicaidIdLegacy && coverages.find(c => (c.coverageId || c.id) === (editingCoverage.coverageId || editingCoverage.id))?.medicaidIdNumber === editingCoverage.medicaidIdNumber && (
                    <p className="mt-1 text-sm text-amber-700">Legacy ID format: re-enter the 9-digit Medicaid ID.</p>
                  )}
                  {validationErrors.medicaidIdNumber && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.medicaidIdNumber}</p>
                  )}
                </div>
              </div>

              {/* Effective and Termination Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Effective Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={editingCoverage.effectiveDate || ''}
                    onChange={(e) => handleInputChange('effectiveDate', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Termination Date
                  </label>
                  <input
                    type="date"
                    value={editingCoverage.terminationDate || ''}
                    onChange={(e) => handleInputChange('terminationDate', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  />
                </div>
              </div>

              {/* State, County and Eligibility Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    State of Eligibility
                  </label>
                  <select
                    value={normalizeStateCode(editingCoverage.stateOfEligibility)}
                    onChange={(e) => handleInputChange('stateOfEligibility', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  >
                    <option value="">Agency's state (default)</option>
                    {US_STATE_OPTIONS.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    County of Eligibility
                  </label>
                  <select
                    value={editingCoverage.countyOfEligibility || ''}
                    onChange={(e) => handleInputChange('countyOfEligibility', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  >
                    <option value="">Select County</option>
                    {texasCountyOptions}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">All 254 Texas counties in alphabetical order</p>
                </div>
              </div>

              {/* Eligibility Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                    Eligibility Category
                  </label>
                  <select
                    value={editingCoverage.eligibilityCategory || 'Unknown'}
                    onChange={(e) => handleInputChange('eligibilityCategory', e.target.value)}
                    disabled={isReadOnlyView}
                    className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                  >
                    <option value="Unknown">Unknown</option>
                    <option value="TANF">TANF (Temporary Assistance for Needy Families)</option>
                    <option value="SSI">SSI (Supplemental Security Income)</option>
                    <option value="CHIP">CHIP</option>
                    <option value="MEDICALLY_NEEDY">Medically Needy</option>
                    <option value="PREGNANT_WOMEN">Pregnant Women</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>

              {/* FFS is paid by TMHP: no MCO (D4) */}
              {editingCoverage.medicaidType === 'FFS' && (
                <p className="text-sm text-slate-600">Payer: TMHP (Texas Medicaid &amp; Healthcare Partnership). Fee-for-service has no MCO.</p>
              )}

              {/* MCO Information Section - Show only for MCO-related Medicaid types */}
              {(editingCoverage.medicaidType === 'MCO' || 
                editingCoverage.medicaidType === 'STAR' || 
                editingCoverage.medicaidType === 'STAR_PLUS' || 
                editingCoverage.medicaidType === 'STAR_KIDS' || 
                editingCoverage.medicaidType === 'STAR_HEALTH') && (
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="text-md font-semibold text-slate-900 mb-4">MCO Information</h4>
                  
                  <div className="space-y-4">
                    {/* MCO Name */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                        MCO {MCO_REQUIRED_PROGRAMS.includes(editingCoverage.medicaidType || '') && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={editingCoverage.medicaidMCO || ''}
                        onChange={(e) => {
                          handleInputChange('medicaidMCO', e.target.value);
                          // Back to the list clears any typed name
                          if (e.target.value !== 'OTHER') handleInputChange('medicaidMCOOther', '');
                        }}
                        disabled={isReadOnlyView}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${validationErrors.medicaidMCO ? 'border-red-500' : 'border-slate-300'} ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                      >
                        <option value="">Select MCO</option>
                        {(settings?.medicaidMcos || [])
                          .filter(m => !m.legacy || m.code === editingCoverage.medicaidMCO)
                          .map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
                        {editingCoverage.medicaidMCO && editingCoverage.medicaidMCO !== 'OTHER'
                          && !(settings?.medicaidMcos || []).some(m => m.code === editingCoverage.medicaidMCO) && (
                          <option value={editingCoverage.medicaidMCO}>{editingCoverage.medicaidMCO}</option>
                        )}
                        <option value="OTHER">Other (type the name)</option>
                      </select>
                      {validationErrors.medicaidMCO && <p className="mt-1 text-sm text-red-600">{validationErrors.medicaidMCO}</p>}
                      {editingCoverage.medicaidMCO === 'OTHER' && (
                        <div className="mt-2">
                          <input
                            type="text"
                            aria-label="MCO name"
                            value={editingCoverage.medicaidMCOOther || ''}
                            onChange={(e) => handleInputChange('medicaidMCOOther', e.target.value)}
                            disabled={isReadOnlyView}
                            className={`w-full px-3 py-2 border rounded-md text-base ${validationErrors.medicaidMCOOther ? 'border-red-500' : 'border-slate-300'}`}
                            placeholder="MCO name"
                            maxLength={120}
                          />
                          {validationErrors.medicaidMCOOther && <p className="mt-1 text-sm text-red-600">{validationErrors.medicaidMCOOther}</p>}
                        </div>
                      )}
                    </div>

                    {/* MCO Member ID */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                        MCO Member ID
                      </label>
                      <input
                        type="text"
                        value={editingCoverage.mcoMemberId || ''}
                        onChange={(e) => handleInputChange('mcoMemberId', e.target.value)}
                        disabled={isReadOnlyView}
                        className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                        placeholder="If different from Medicaid ID"
                      />
                    </div>

                    {/* MCO Phones */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          MCO Customer Service Phone
                        </label>
                        <input
                          type="tel"
                          value={editingCoverage.mcoCustomerServicePhone || ''}
                          onChange={(e) => handleInputChange('mcoCustomerServicePhone', e.target.value)}
                          onBlur={() => validateField('mcoCustomerServicePhone')}
                          disabled={isReadOnlyView}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                            validationErrors.mcoCustomerServicePhone ? 'border-red-500' : 'border-slate-300'
                          } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          placeholder="(XXX) XXX-XXXX"
                        />
                        {validationErrors.mcoCustomerServicePhone && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.mcoCustomerServicePhone}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          MCO Prior Auth Phone
                        </label>
                        <input
                          type="tel"
                          value={editingCoverage.mcoPriorAuthPhone || ''}
                          onChange={(e) => handleInputChange('mcoPriorAuthPhone', e.target.value)}
                          onBlur={() => validateField('mcoPriorAuthPhone')}
                          disabled={isReadOnlyView}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                            validationErrors.mcoPriorAuthPhone ? 'border-red-500' : 'border-slate-300'
                          } ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          placeholder="(XXX) XXX-XXXX"
                        />
                        {validationErrors.mcoPriorAuthPhone && (
                          <p className="mt-1 text-sm text-red-600">{validationErrors.mcoPriorAuthPhone}</p>
                        )}
                      </div>
                    </div>

                    {/* Same as Customer Service Checkbox */}
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editingCoverage.mcoPriorAuthPhone === editingCoverage.mcoCustomerServicePhone && !!editingCoverage.mcoCustomerServicePhone}
                          onChange={(e) => {
                            if (e.target.checked && editingCoverage.mcoCustomerServicePhone) {
                              handleInputChange('mcoPriorAuthPhone', editingCoverage.mcoCustomerServicePhone);
                            }
                          }}
                          disabled={isReadOnlyView}
                          className={`w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                        />
                        <span className="text-sm text-slate-700">Same as Customer Service</span>
                      </label>
                    </div>

                    {/* Case Manager Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          Case Manager Name
                        </label>
                        <input
                          type="text"
                          value={editingCoverage.caseManagerName || ''}
                          onChange={(e) => handleInputChange('caseManagerName', e.target.value)}
                          disabled={isReadOnlyView}
                          className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          placeholder="Enter case manager name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                          Case Manager Phone
                        </label>
                        <input
                          type="tel"
                          value={editingCoverage.caseManagerPhone || ''}
                          onChange={(e) => handleInputChange('caseManagerPhone', e.target.value)}
                          disabled={isReadOnlyView}
                          className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                          placeholder="XXX-XXX-XXXX"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Information Section */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="text-md font-semibold text-slate-900 mb-4">Additional Information</h4>
                
                <div className="space-y-4">
                  {/* Is this Primary Insurance */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 text-left">
                      Is this Primary Insurance? <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-6">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="isPrimaryMedicaid"
                          checked={editingCoverage.cobOrder === COBOrder.PRIMARY}
                          onChange={() => handleInputChange('cobOrder', COBOrder.PRIMARY)}
                          disabled={isReadOnlyView}
                          className={`w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                        />
                        <span className="text-sm text-slate-700">Yes</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="isPrimaryMedicaid"
                          checked={editingCoverage.cobOrder !== COBOrder.PRIMARY}
                          onChange={() => handleInputChange('cobOrder', COBOrder.SECONDARY)}
                          disabled={isReadOnlyView}
                          className={`w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500 ${isReadOnlyView ? 'opacity-60 cursor-not-allowed' : ''}`}
                        />
                        <span className="text-sm text-slate-700">No</span>
                      </label>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1 text-left">
                      Notes
                    </label>
                    <textarea
                      value={editingCoverage.notes || ''}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      disabled={isReadOnlyView}
                      rows={4}
                      className={`w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base ${isReadOnlyView ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-60' : ''}`}
                      placeholder="Enter any additional notes"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* VERIFICATION (REQ-INS-10/11): saved coverages only */}
          {(editingCoverage.coverageId || editingCoverage.id) && (
            <div className="border-t border-slate-200 pt-4" data-testid="verification-section">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-md font-semibold text-slate-900">Eligibility Verification</h4>
                {canVerifyInsurance && (
                  <button
                    type="button"
                    onClick={() => { setDialogError(null); setVerifyTarget(editingCoverage); }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm border border-slate-300 bg-white hover:bg-slate-50"
                  >
                    <ClipboardCheck size={16} /> Log verification
                  </button>
                )}
              </div>
              <p className="text-sm">
                Last verified:{' '}
                <span className={editingCoverage.verificationDue ? 'px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium' : 'font-medium'}>
                  {editingCoverage.lastVerifiedAt ? formatDate(editingCoverage.lastVerifiedAt) : 'Never'}
                </span>
                {editingCoverage.verificationDue && editingCoverage.coverageType === CoverageType.MEDICAID && (
                  <span className="ml-2 text-amber-700">Medicaid eligibility can change monthly: verify again.</span>
                )}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Verify eligibility manually in the TMHP or payer portal (or by phone or fax), then log it here.
              </p>
              {(editingCoverage.verificationLog || []).length > 0 && (
                <ul className="mt-3 divide-y divide-slate-200 border border-slate-200 rounded-md text-sm">
                  {(editingCoverage.verificationLog || []).map(v => (
                    <li key={v.id} className="px-3 py-2">
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        <span className="font-medium">{formatDate(v.verifiedDate)}</span>
                        <span>{v.method.charAt(0) + v.method.slice(1).toLowerCase()}</span>
                        <span>PDN covered: {v.pdnCovered === 'Y' ? 'Yes' : 'No'}</span>
                        <span>Prior auth: {v.priorAuthRequired === 'Y' ? 'Required' : 'Not required'}</span>
                        {v.hourOrVisitLimit && <span>Limit: {v.hourOrVisitLimit}</span>}
                        {v.referenceNumber && <span>Ref #{v.referenceNumber}</span>}
                      </div>
                      {(v.contactName || v.deductible || v.copay || v.note) && (
                        <div className="mt-0.5 text-slate-600">
                          {[v.contactName && `Spoke to ${v.contactName}`, v.deductible && `Deductible ${v.deductible}`, v.copay && `Copay ${v.copay}`, v.note].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {(editingCoverage.verificationNotes || editingCoverage.lastVerifiedDate) && (
                <p className="mt-2 text-xs text-slate-500">
                  Legacy note{editingCoverage.lastVerifiedDate ? ` (${formatDate(editingCoverage.lastVerifiedDate)})` : ''}: {editingCoverage.verificationNotes || editingCoverage.verificationStatus || ''}
                </p>
              )}
            </div>
          )}
        </div>

      </FormPageLayout>
    );
  }, [editingCoverage, showErrorSummary, validationErrors, cancelForm, handleInputChange,
      handleMedicareAdvantageChange, handlePartDChange, handleMedigapChange,
      validateField, handleSave, loading, canReadInsurance, isReadOnlyView,
      settings, coverages, canVerifyInsurance, formatDate, setSubscriberAddress, texasCountyOptions]);

  // Payer type options for dropdown
  const payerTypeOptions = useMemo(() => [
    {
      label: 'Private Insurance',
      value: CoverageType.PRIVATE,
      description: 'Commercial, employer-sponsored',
      onClick: () => handleSelectPayerType(CoverageType.PRIVATE)
    },
    {
      label: 'Medicaid',
      value: CoverageType.MEDICAID,
      description: 'State Medicaid program',
      onClick: () => handleSelectPayerType(CoverageType.MEDICAID)
    },
    {
      label: 'Medicare',
      value: CoverageType.MEDICARE,
      description: 'Original Medicare (Parts A & B)',
      onClick: () => handleSelectPayerType(CoverageType.MEDICARE)
    },
    {
      label: 'Medicare Advantage',
      value: 'MEDICARE_ADVANTAGE',
      description: 'Medicare Part C',
      onClick: () => handleSelectPayerType(CoverageType.MEDICARE)
    },
    {
      label: 'Other',
      value: CoverageType.OTHER,
      description: 'Any other payer (type the name)',
      onClick: () => handleSelectPayerType(CoverageType.OTHER)
    }
  ], [handleSelectPayerType]);

  // Memoize error dismiss handler
  const handleDismissError = useCallback(() => setError(null), []);

  return (
    <>
      {showAddForm ? formContent : null}
      
      {!showAddForm && (
        <GenericListView
            // ListView props
            title="Insurance and Coverage"
            icon={<Shield size={20} />}
            expanded={expanded}
            onToggleExpand={toggleExpanded}
            alertBanner={alertBanner}
            // Data props
            items={coverages}
            keyExtractor={keyExtractor}
            columns={columns}
            actions={actions}
            // Mobile card config
            mobileCard={mobileCard}
            // Action bar props
            onRefresh={handleRefresh}
            refreshing={refreshing}
            loading={loading}
            newButtonLabel="Add Payer"
            newButtonOptions={payerTypeOptions}
            showNew={canCreateInsurance}
            // State props
            error={error}
            onDismissError={handleDismissError}
            hasLoadedOnce={hasLoadedOnce}
            // Empty state props
            emptyStateIcon={emptyStateIcon}
            emptyStateTitle="No insurance coverage available"
            emptyStateMessage="Start by adding a new insurance coverage using the button above."
        />
      )}

      <TerminateCoverageDialog
        open={Boolean(terminateTarget)}
        payerLabel={terminateTarget ? coverageLabel(terminateTarget) : ''}
        effectiveDate={terminateTarget?.effectiveDate || today}
        today={today}
        busy={dialogBusy}
        error={dialogError}
        onConfirm={handleTerminate}
        onCancel={closeDialogs}
      />
      <LogVerificationDialog
        open={Boolean(verifyTarget)}
        today={today}
        payerLabel={verifyTarget ? coverageLabel(verifyTarget) : ''}
        busy={dialogBusy}
        error={dialogError}
        onConfirm={handleLogVerification}
        onCancel={closeDialogs}
      />
    </>
  );
};

export default React.memo(Insurance);
